import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Clock,
  BarChart3,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Mail,
  Building,
  Mic
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ParticipantTracking = () => {
  const { meetingId } = useParams();
  const [participants, setParticipants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newParticipant, setNewParticipant] = useState({
    name: '',
    email: '',
    role: 'attendee',
    department: ''
  });

  const token = sessionStorage.getItem('auth_token');

  const fetchParticipants = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/meetings/${meetingId}/participants?token=${token}`
      );
      const data = await response.json();
      if (data.success) {
        setParticipants(data.participants || []);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  }, [meetingId, token]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/meetings/${meetingId}/participants/stats?token=${token}`
      );
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [meetingId, token]);

  // Progressive — participants + stats paint independently.
  useEffect(() => {
    setLoading(true);
    const tasks = [fetchParticipants(), fetchStats()].map((p) =>
      Promise.resolve(p).catch((e) => { console.error('[ParticipantTracking]', e); return null; }));
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  }, [fetchParticipants, fetchStats]);

  const handleAddParticipant = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/meetings/${meetingId}/participants?token=${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newParticipant)
        }
      );
      const data = await response.json();
      if (data.success) {
        toast.success('Participant added');
        setNewParticipant({ name: '', email: '', role: 'attendee', department: '' });
        setShowAddForm(false);
        fetchParticipants();
      }
    } catch (error) {
      toast.error('Failed to add participant');
    }
  };

  const handleExtractFromTranscript = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/meetings/${meetingId}/participants/extract-from-transcript?token=${token}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (data.success) {
        toast.success(`Extracted ${data.participants_created} participants`);
        fetchParticipants();
      } else {
        toast.error(data.error || 'Extraction failed');
      }
    } catch (error) {
      toast.error('Failed to extract participants');
    }
  };

  const handleSyncFromDaily = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/meetings/${meetingId}/participants/sync-from-daily?token=${token}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (data.success) {
        toast.success(`Synced ${data.participants_synced} participants from Daily.co`);
        fetchParticipants();
      }
    } catch (error) {
      toast.error('Failed to sync from Daily.co');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'host': return 'bg-purple-100 text-purple-800';
      case 'presenter': return 'bg-blue-100 text-blue-800';
      case 'observer': return 'bg-gray-100 text-gray-600';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'joined': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'left': return <XCircle className="h-4 w-4 text-orange-500" />;
      case 'no_show': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="participants-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="participant-tracking">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Participant Tracking</h2>
          <p className="text-gray-500 text-sm">Track attendance and participation metrics</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncFromDaily}
            data-testid="sync-daily-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync from Daily
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExtractFromTranscript}
            data-testid="extract-transcript-btn"
          >
            <Mic className="h-4 w-4 mr-2" />
            Extract from Transcript
          </Button>
          <Button
            size="sm"
            onClick={() => setShowAddForm(true)}
            data-testid="add-participant-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add Participant
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4" data-testid="participants-stats">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Users className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.total_participants}</p>
                  <p className="text-sm text-gray-500">Total Participants</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.joined}</p>
                  <p className="text-sm text-gray-500">Joined</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <XCircle className="h-8 w-8 text-red-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.no_show}</p>
                  <p className="text-sm text-gray-500">No Shows</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <BarChart3 className="h-8 w-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.attendance_rate}%</p>
                  <p className="text-sm text-gray-500">Attendance Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Participant Form */}
      {showAddForm && (
        <Card data-testid="add-participant-form">
          <CardHeader>
            <CardTitle>Add Participant</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddParticipant} className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="text-sm text-gray-500">Name *</label>
                <Input
                  value={newParticipant.name}
                  onChange={(e) => setNewParticipant({ ...newParticipant, name: e.target.value })}
                  placeholder="Participant name"
                  required
                  data-testid="participant-name-input"
                />
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-500">Email</label>
                <Input
                  type="email"
                  value={newParticipant.email}
                  onChange={(e) => setNewParticipant({ ...newParticipant, email: e.target.value })}
                  placeholder="email@example.com"
                  data-testid="participant-email-input"
                />
              </div>
              <div className="w-40">
                <label className="text-sm text-gray-500">Role</label>
                <select
                  value={newParticipant.role}
                  onChange={(e) => setNewParticipant({ ...newParticipant, role: e.target.value })}
                  className="w-full h-10 px-3 border rounded-md"
                  data-testid="participant-role-select"
                >
                  <option value="host">Host</option>
                  <option value="presenter">Presenter</option>
                  <option value="attendee">Attendee</option>
                  <option value="observer">Observer</option>
                </select>
              </div>
              <Button type="submit" data-testid="save-participant-btn">Save</Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Participants List */}
      <Card data-testid="participants-list">
        <CardHeader>
          <CardTitle>Participants ({participants.length})</CardTitle>
          <CardDescription>Meeting attendees and their participation metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="divide-y">
            {participants.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No participants recorded yet. Add participants or extract from transcript.
              </div>
            ) : (
              participants.map((participant, index) => (
                <div
                  key={participant.id || index}
                  className="py-4 flex items-center justify-between"
                  data-testid={`participant-row-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-medium">
                        {(participant.name || 'U')[0].toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{participant.name}</span>
                        {getStatusIcon(participant.attendance_status)}
                        <Badge className={getRoleBadgeColor(participant.role)}>
                          {participant.role || 'attendee'}
                        </Badge>
                        {participant.is_project_member && (
                          <Badge variant="outline">Project Member</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        {participant.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {participant.email}
                          </span>
                        )}
                        {participant.department && (
                          <span className="flex items-center gap-1">
                            <Building className="h-3 w-3" />
                            {participant.department}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {participant.speaking_duration_seconds > 0 && (
                      <div>
                        <span className="text-lg font-semibold text-blue-600">
                          {formatDuration(participant.speaking_duration_seconds)}
                        </span>
                        <p className="text-xs text-gray-500">Speaking time</p>
                      </div>
                    )}
                    {participant.speaking_percentage > 0 && (
                      <div className="mt-1">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${Math.min(participant.speaking_percentage, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {participant.speaking_percentage}% of meeting
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Top Speakers */}
      {stats?.top_speakers?.length > 0 && (
        <Card data-testid="top-speakers">
          <CardHeader>
            <CardTitle>Top Speakers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.top_speakers.slice(0, 5).map((speaker, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-lg font-bold text-gray-400 w-6">
                    #{index + 1}
                  </span>
                  <div className="flex-1">
                    <span className="font-medium">{speaker.name}</span>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                          style={{ width: `${speaker.speaking_percentage || 0}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-500 w-16 text-right">
                        {formatDuration(speaker.speaking_duration_seconds)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ParticipantTracking;
